import { Button } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Beneficiaries } from 'BreetComponents';
import { type IBeneficiary } from 'BreetHelpers';
import { useAutoSettlementApi } from 'BreetHooks';
import { selectSelectedBeneficiary, useAppSelector } from 'BreetRedux';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
	autoSettlementSchema,
	type AutoSettlementType,
	type IAutoSettlementFormComponent,
} from './DialogManageAutoSettlementAccount.helpers';

const FormComponent = ({ onAddNewBeneficiary, onManageBeneficiaries }: IAutoSettlementFormComponent) => {
	const existingBeneficiary = useAppSelector(selectSelectedBeneficiary);

	const defaultSelectedBeneficiary = {
		accountName: existingBeneficiary?.accountName ?? '',
		accountNumber: existingBeneficiary?.accountNumber ?? '',
		bankName: existingBeneficiary?.bankName ?? '',
		id: existingBeneficiary?._id ?? '',
	};

	const { setValue, watch, handleSubmit } = useForm<AutoSettlementType>({
		resolver: yupResolver(autoSettlementSchema),
		mode: 'onChange',
		defaultValues: {
			selectedBeneficiary: defaultSelectedBeneficiary,
		},
	});

	const selectBeneficiary = useCallback(
		(selectedBeneficiaryItem: IBeneficiary | undefined) => {
			if (!selectedBeneficiaryItem) return;
			setValue(
				'selectedBeneficiary',
				{
					accountName: selectedBeneficiaryItem.accountName,
					accountNumber: selectedBeneficiaryItem.accountNumber,
					bankName: selectedBeneficiaryItem.bankName,
					id: selectedBeneficiaryItem._id,
				},
				{ shouldValidate: true }
			);
		},
		[setValue]
	);

	const selectedBeneficiary = watch('selectedBeneficiary');
	const selectedBeneficiaryId = selectedBeneficiary.id;

	const isSelectedBeneficiaryAlreadyEnabled = !!existingBeneficiary && existingBeneficiary._id === selectedBeneficiaryId;

	const handleAutomaticSettlement = useAutoSettlementApi({
		accountName: selectedBeneficiary.accountName,
		accountNumber: selectedBeneficiary.accountNumber,
		bankName: selectedBeneficiary.bankName,
		id: selectedBeneficiary.id,
		autoSettlement: isSelectedBeneficiaryAlreadyEnabled && existingBeneficiary.autoSettlement,
	});

	return (
		<form
			onSubmit={handleSubmit(handleAutomaticSettlement)}
			className='payment-request-form'
			noValidate
		>
			<Beneficiaries
				onAddNewBeneficiary={onAddNewBeneficiary}
				selectBeneficiary={selectBeneficiary}
				selectedBeneficiaryId={watch('selectedBeneficiary').id}
				key={watch('selectedBeneficiary').id}
				title='Select Destination'
				onManageBeneficiaries={onManageBeneficiaries}
				filterOutType='virtual_account'
			/>

			<div className='payment-request-form_cta'>
				<Button
					label={isSelectedBeneficiaryAlreadyEnabled ? 'Disable' : 'Enable'}
					type='submit'
					variant={isSelectedBeneficiaryAlreadyEnabled ? 'tertiary' : 'primary'}
					className='right-sheet_cta_fullwidth'
					disabled={!selectedBeneficiaryId}
				/>
			</div>
		</form>
	);
};

export default FormComponent;
